import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { InviteUsersModal } from '@/components/InviteUsersModal'
import { useSendInvites } from '@/hooks/queries/crt/useInvites'
import { useRoles } from '@/hooks/queries/crt/useRoles'
import { useUsers } from '@/hooks/queries/crt/useUsers'
import { usersSearchSchema } from '@/hooks/queries/useUsers'
import { useTablePagination } from '@/hooks/useTablePagination'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { HStack } from '@/components/Atoms'
import { Button } from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { BellIcon, UserRoundPlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import UsersDataTable from './-components/UsersDataTable'

export const Route = createFileRoute('/_auth/_modules/crt/admin/users/')({
  component: UsersPage,
  validateSearch: (search) => usersSearchSchema.parse(search),
})

function UsersPage() {
  const { t } = useTranslation('admin')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  const {
    isPending: isUsersPending,
    data: { users, count } = { users: [], count: 0 },
    error: usersError,
  } = useUsers({ ...search, page: search.page ?? 1, pageSize })
  const { isPending: isRolesPending, data: roles, error: rolesError } = useRoles()

  return (
    <PageContainer>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.INVITATIONS}
        title={t('tutorials.dialog.users.title')}
        guideUrl="/admin/users"
      >
        {t('tutorials.dialog.users.description')}
      </IntroductionDialog>

      <Spread>
        <PageHeader>{t('usersPage.header')}</PageHeader>
        <HStack gap="sm">
          <PermissionsGuard required={['crt_notifications_view']}>
            <Button type="button" variant="secondary" asChild>
              <Link to="/crt/admin/notifications">
                <BellIcon /> {t('usersPage.configureNotifications')}
              </Link>
            </Button>
          </PermissionsGuard>
          <PermissionsGuard required={['crt_invites_send']}>
            <InviteUsersModal
              roles={roles}
              useSendInvitesHook={useSendInvites}
              trigger={
                <Button>
                  <UserRoundPlusIcon /> {t('usersPage.inviteUsersModalTrigger')}
                </Button>
              }
            />
          </PermissionsGuard>
        </HStack>
      </Spread>

      <Card>
        <CardHeader>
          <Spread>
            <CardTitle>{t('usersPage.title')}</CardTitle>
          </Spread>
        </CardHeader>

        <CardContent>
          <UsersDataTable
            users={users}
            roles={roles}
            count={count}
            isPending={isUsersPending || isRolesPending}
            error={usersError || rolesError}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
